import { useState, useCallback, useRef } from "react";
import styles from "./FormLayout.module.scss";

import Form1 from "./Form1";
import Form2 from "./Form2";
import Form3 from "./Form3";
import Form4 from "./Form4";
import Form5 from "./Form5";

import {
  FaUser,
  FaBriefcase,
  FaBullseye,
  FaGraduationCap,
  FaFileAlt,
  FaTimes,
} from "react-icons/fa";

import { MyModal } from "../../../utility";
import ModalContainer from "../../../components/modal_popup";

/* ───────────────── TABS ───────────────── */

const tabs = [
  {
    id: "basic",
    label: "Basic Details",
    icon: <FaUser />,
  },
  {
    id: "work",
    label: "Work Experience",
    icon: <FaBriefcase />,
  },
  {
    id: "skills",
    label: "Key Skills",
    icon: <FaBullseye />,
  },
  {
    id: "education",
    label: "Education",
    icon: <FaGraduationCap />,
  },
  {
    id: "additional",
    label: "Additional",
    icon: <FaFileAlt />,
  },
];

const TAB_ORDER = [
  "basic",
  "work",
  "skills",
  "education",
  "additional",
];

const STEP_TO_TAB = {
  1: "basic",
  2: "work",
  3: "skills",
  4: "education",
  5: "additional",
};

export default function FormMainLayout({
  onClose,
  leadId,
  canId,
  dataType,
  initialStep = 1,
  onSuccess,
}) {
  /* ───────────────── COMMON ───────────────── */

  const id = leadId || canId;

  const recordType =
    dataType || (leadId ? "can_lead" : "candidate");

  /* ───────────────── STATES ───────────────── */

  const [activeTab, setActiveTab] = useState(
    STEP_TO_TAB[initialStep] || "basic"
  );

  const [form1Data, setForm1Data] = useState(null);
  const [form2Data, setForm2Data] = useState(null);
  const [form3Data, setForm3Data] = useState(null);
  const [form4Data, setForm4Data] = useState(null);

  const isEditMode =
    !!id && recordType === "candidate";

  const [isComplete, setIsComplete] =
    useState(false);

  const [showErrorMsg, setShowErrorMsg] =
    useState(false);

  const [firstErrorForm, setFirstErrorForm] =
    useState(null);

  const [visitedTabs, setVisitedTabs] =
    useState(new Set(["basic"]));

  /* ───────────────── VALIDATED ───────────────── */

  const [validatedTabs, setValidatedTabs] =
    useState({
      basic: false,
      work: false,
      skills: false,
      education: false,
      additional: false,
    });

  /* ───────────────── SUCCESS ONLY AFTER SAVE ───────────────── */

  const [submittedTabs, setSubmittedTabs] =
    useState({
      basic: false,
      work: false,
      skills: false,
      education: false,
      additional: false,
    });

  /* ───────────────── ERRORS ───────────────── */

  const [formErrors, setFormErrors] =
    useState({
      basic: false,
      work: false,
      skills: false,
      education: false,
      additional: false,
    });

  /* ───────────────── VALIDATION FUNCTIONS ───────────────── */

  const validationFunctions = useRef({
    basic: null,
    work: null,
    skills: null,
    education: null,
    additional: null,
  });

  /* ───────────────── REGISTER VALIDATION ───────────────── */

  const registerValidation = useCallback(
    (formId, validateFn) => {
      validationFunctions.current[formId] =
        validateFn;
    },
    []
  );

  /* ───────────────── UPDATE VALIDATION ───────────────── */

  const updateFormValidation = useCallback(
    (formId) => {
      const validateFn =
        validationFunctions.current[formId];

      if (validateFn) {
        const validation = validateFn(true);

        const isValid =
          typeof validation === "object"
            ? validation.isValid
            : validation;

        setValidatedTabs((prev) => ({
          ...prev,
          [formId]: isValid,
        }));

        setFormErrors((prev) => ({
          ...prev,
          [formId]: !isValid,
        }));

        return isValid;
      }

      return false;
    },
    []
  );

  /* ───────────────── VALIDATE ALL ───────────────── */

  const validateAllFormsSync = useCallback(() => {
    const results = {};

    let allValid = true;

    for (const formId of TAB_ORDER) {
      const validateFn =
        validationFunctions.current[formId];

      if (validateFn) {
        const validation = validateFn(false);

        const isValid =
          typeof validation === "object"
            ? validation.isValid
            : validation;

        results[formId] = isValid;

        if (!isValid) {
          allValid = false;
        }
      } else {
        results[formId] = false;
        allValid = false;
      }
    }

    return { allValid, results };
  }, []);

  /* ───────────────── CAN NAVIGATE ───────────────── */

  const canNavigateToTab = useCallback(
    (targetTabId) => {

      // UPDATE MODE → OPEN ALL TABS
      if (isEditMode) {
        return true;
      }

      const targetIndex =
        TAB_ORDER.indexOf(targetTabId);

      // FIRST TAB ALWAYS OPEN
      if (targetIndex === 0) {
        return true;
      }

      // PREVIOUS TAB MUST SAVE
      const prevTab =
        TAB_ORDER[targetIndex - 1];

      return submittedTabs[prevTab];

    },
    [submittedTabs, isEditMode]
  );

  /* ───────────────── TAB CHANGE ───────────────── */

  const handleTabChange = (tabId) => {
    if (activeTab === tabId) return;

    // LOCK CHECK
    if (!canNavigateToTab(tabId)) {
      const targetIndex =
        TAB_ORDER.indexOf(tabId);

      const prevTab =
        TAB_ORDER[targetIndex - 1];

      setFirstErrorForm(prevTab);

      setShowErrorMsg(true);

      return;
    }

    setVisitedTabs(
      (prev) =>
        new Set([...prev, activeTab])
    );

    setActiveTab(tabId);
  };

  /* ───────────────── FORM SUBMIT ───────────────── */

  const handleFormSubmit = async (
    formId,
    data,
    nextTab,
    isAutoSave = false
  ) => {
    /* SAVE DATA */

    if (formId === "basic") {
      setForm1Data(data);
    }

    if (formId === "work") {
      setForm2Data(data);
    }

    if (formId === "skills") {
      setForm3Data(data);
    }

    if (formId === "education") {
      setForm4Data(data);
    }

    setVisitedTabs(
      (prev) =>
        new Set([...prev, formId])
    );

    if (isAutoSave) return;

    /* VALIDATE */

    const isValid =
      updateFormValidation(formId);

    if (isValid) {
      /* SUCCESS */

      setSubmittedTabs((prev) => ({
        ...prev,
        [formId]: true,
      }));

      setValidatedTabs((prev) => ({
        ...prev,
        [formId]: true,
      }));

      setFormErrors((prev) => ({
        ...prev,
        [formId]: false,
      }));

      /* NEXT TAB */

      if (nextTab) {
        setActiveTab(nextTab);
      }
    } else {
      /* FAIL */

      setSubmittedTabs((prev) => ({
        ...prev,
        [formId]: false,
      }));

      setValidatedTabs((prev) => ({
        ...prev,
        [formId]: false,
      }));

      setFormErrors((prev) => ({
        ...prev,
        [formId]: true,
      }));

      alert(
        "Please fill all required fields before proceeding."
      );
    }
  };

  /* ───────────────── BACK ───────────────── */

  const handleBack = (
    currentTab,
    prevTab
  ) => {
    updateFormValidation(currentTab);

    setActiveTab(prevTab);
  };

  /* ───────────────── FINAL SUBMIT ───────────────── */

  // In FormMainLayout.jsx, update the handleFinalSubmit function:

  const handleFinalSubmit = async (data) => {
    if (data?.triggerConfirm) {
      // MARK ADDITIONAL SAVED
      const updatedSubmittedTabs = {
        ...submittedTabs,
        additional: true,
      };

      setSubmittedTabs(updatedSubmittedTabs);

      // CHECK UNSAVED
      const unsavedTabs = isEditMode
        ? []
        : TAB_ORDER.filter(
          (tab) => !updatedSubmittedTabs[tab]
        );

      if (unsavedTabs.length > 0) {
        alert(
          `Please save all sections before final submit.\n\nMissing:\n${unsavedTabs.join("\n")}`
        );
        setActiveTab(unsavedTabs[0]);
        return;
      }

      // VALIDATE ALL
      const { allValid, results } = validateAllFormsSync();

      if (!allValid) {
        const firstInvalidForm = TAB_ORDER.find(
          (key) => results[key] === false
        );
        setFirstErrorForm(firstInvalidForm);
        setShowErrorMsg(true);
        if (firstInvalidForm) {
          setActiveTab(firstInvalidForm);
        }
        return;
      }

      data.confirmCallback();
      return;
    }

    // For edit mode, just close without showing success message
    if (data?.isEditMode) {
      setIsComplete(false); // Don't show success message

      if (onSuccess && typeof onSuccess === "function") {
        onSuccess();
      }

      if (onClose) {
        onClose();
      }
      return;
    }

    // For new registration, show success message
    setIsComplete(true);

    if (onSuccess && typeof onSuccess === "function") {
      onSuccess();
    }

    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 2000);
  };
  /* ───────────────── CLOSE ERROR MODAL ───────────────── */

  const handleCloseErrorModal = () => {
    setShowErrorMsg(false);

    setFirstErrorForm(null);
  };

  /* ───────────────── COMMON PROPS ───────────────── */

  const commonProps = {
    onClose,
    id,
    recordType,
  };

  /* ───────────────── UI ───────────────── */

  return (
    <div className={styles.layoutWrapper}>
      <div className={styles.layoutCard}>
        {/* HEADER */}

        <div className={styles.layoutHeader}>
          <div className={styles.headerTitle}>
            <h1>
              {recordType === "candidate"
                ? "Edit Candidate Details"
                : "Candidate Registration Form"}
            </h1>

            <button
              className={styles.closeButton}
              onClick={onClose}
            >
              <FaTimes />
            </button>
          </div>

          {isComplete && (
            <div
              className={
                styles.successMessage
              }
            >
              ✅ Registration Completed
              Successfully!
            </div>
          )}
        </div>

        {/* BODY */}

        <div className={styles.layoutBody}>
          {/* SIDEBAR */}

          <nav className={styles.sidebar}>
            {tabs.map((t) => {
              const hasError =
                formErrors[t.id] &&
                visitedTabs.has(t.id);

              const isActive =
                activeTab === t.id;

              // SUCCESS ONLY AFTER SAVE
              const isValidated =
                submittedTabs[t.id];

              // LOCK
              const isLocked =
                !isEditMode &&
                !canNavigateToTab(t.id) &&
                !isActive;

              return (
                <button
                  key={t.id}
                  className={`
                    ${styles.tabBtn}
                    ${isActive
                      ? styles.active
                      : ""
                    }
                    ${hasError
                      ? styles.hasError
                      : ""
                    }
                    ${!hasError &&
                      isValidated &&
                      !isActive
                      ? styles.hasSuccess
                      : ""
                    }
                    ${isLocked
                      ? styles.locked
                      : ""
                    }
                  `}
                  onClick={() =>
                    handleTabChange(t.id)
                  }
                  disabled={isLocked}
                >
                  {/* ICON */}

                  <span
                    className={
                      styles.tabIcon
                    }
                  >
                    {t.icon}
                  </span>

                  {/* LABEL */}

                  <span
                    className={
                      styles.tabLabel
                    }
                  >
                    {t.label}
                  </span>

                  {/* ERROR */}

                  {hasError && (
                    <span
                      className={
                        styles.errorBadge
                      }
                    >
                      !
                    </span>
                  )}

                  {/* SUCCESS */}

                  {!hasError &&
                    isValidated && (
                      <span
                        className={
                          styles.successBadge
                        }
                      >
                        ✓
                      </span>
                    )}

                  {/* LOCK */}

                  {isLocked && (
                    <span
                      className={
                        styles.lockedBadge
                      }
                    >
                      🔒
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* CONTENT */}

          <div className={styles.contentArea}>
            {/* FORM 1 */}

            <div
              style={{
                display:
                  activeTab === "basic"
                    ? "block"
                    : "none",
              }}
            >
              <Form1
                {...commonProps}
                onSubmit={(
                  data,
                  isAutoSave
                ) =>
                  handleFormSubmit(
                    "basic",
                    data,
                    "work",
                    isAutoSave
                  )
                }
                registerValidation={(
                  fn
                ) =>
                  registerValidation(
                    "basic",
                    fn
                  )
                }
              />
            </div>

            {/* FORM 2 */}

            <div
              style={{
                display:
                  activeTab === "work"
                    ? "block"
                    : "none",
              }}
            >
              <Form2
                {...commonProps}
                form1Data={form1Data}
                onSubmit={(
                  data,
                  isAutoSave
                ) =>
                  handleFormSubmit(
                    "work",
                    data,
                    "skills",
                    isAutoSave
                  )
                }
                onBack={() =>
                  handleBack(
                    "work",
                    "basic"
                  )
                }
                registerValidation={(
                  fn
                ) =>
                  registerValidation(
                    "work",
                    fn
                  )
                }
              />
            </div>

            {/* FORM 3 */}

            <div
              style={{
                display:
                  activeTab === "skills"
                    ? "block"
                    : "none",
              }}
            >
              <Form3
                {...commonProps}
                form2Data={form2Data}
                onSubmit={(
                  data,
                  isAutoSave
                ) =>
                  handleFormSubmit(
                    "skills",
                    data,
                    "education",
                    isAutoSave
                  )
                }
                onBack={() =>
                  handleBack(
                    "skills",
                    "work"
                  )
                }
                registerValidation={(
                  fn
                ) =>
                  registerValidation(
                    "skills",
                    fn
                  )
                }
              />
            </div>

            {/* FORM 4 */}

            <div
              style={{
                display:
                  activeTab ===
                    "education"
                    ? "block"
                    : "none",
              }}
            >
              <Form4
                {...commonProps}
                form3Data={form3Data}
                onSubmit={(
                  data,
                  isAutoSave
                ) =>
                  handleFormSubmit(
                    "education",
                    data,
                    "additional",
                    isAutoSave
                  )
                }
                onBack={() =>
                  handleBack(
                    "education",
                    "skills"
                  )
                }
                registerValidation={(
                  fn
                ) =>
                  registerValidation(
                    "education",
                    fn
                  )
                }
              />
            </div>

            {/* FORM 5 */}

            <div
              style={{
                display:
                  activeTab === "additional"
                    ? "block"
                    : "none",
              }}
            >
              <Form5
                {...commonProps}
                form4Data={form4Data}
                onSubmit={handleFinalSubmit}
                onBack={() =>
                  handleBack(
                    "additional",
                    "education"
                  )
                }
                registerValidation={(fn) =>
                  registerValidation(
                    "additional",
                    fn
                  )
                }
                onSuccess={onSuccess}
                isEditMode={!!id && recordType === "candidate"}
              />
            </div>
          </div>

          {/* ERROR MODAL */}

          {showErrorMsg && (
            <MyModal>
              <ModalContainer
                zIndex={1500}
                childComponent={
                  <div
                    className={
                      styles.errorModalContent
                    }
                  >
                    <div
                      className={
                        styles.errorMessage
                      }
                    >
                      Please complete and
                      save{" "}
                      <b>
                        {
                          tabs.find(
                            (t) =>
                              t.id ===
                              firstErrorForm
                          )?.label
                        }
                      </b>{" "}
                      section before moving
                      to next section.
                    </div>

                    <div
                      style={{
                        textAlign:
                          "center",
                      }}
                    >
                      <button
                        className={
                          styles.okButton
                        }
                        onClick={
                          handleCloseErrorModal
                        }
                      >
                        OK
                      </button>
                    </div>
                  </div>
                }
              />
            </MyModal>
          )}
        </div>
      </div>
    </div>
  );
}